/* global GcHtml5 */
import axios from 'axios';
import { reasonName } from '../utils/reasonCodes';

/**
 * GeoComply BrowserGuard Service
 * Implements the integration guide pattern with periodic checks and MyIP service.
 */
class BrowserGuardService {
  constructor() {
    this.client = null;
    this.license = null;
    this.userId = null;
    // GeoComply operator (e.g. 'LatencyTestBR1') chosen at login. Determines
    // which license, encryption keys and cluster the SDK + backend use.
    this.operator = null;
    this.isInitialized = false;

    // Periodic check state
    this.initGeolocationPerformed = false;
    this.timerId = null;
    this.timerStartTime = null;
    this.timerInterval = null;
     

    // MyIP service state
    this.isMyIpServiceRunning = false;
    this.ipChangePending = false; // Flag for background IP changes

    this.isProcessingSuccess = false; // Flag to ignore spurious failure events if success already fired
    this.isStopped = false; // Flag to kill the loop on failure

    // Pending request for UI feedback (e.g. Login)
    this._pendingPromise = null;

    // Event listeners for background updates
    this.listeners = [];

    // Track the reason for the last request to inform subscribers
    this.lastReasonCode = null;

    // Last full geo response for debug panel
    this.lastGeoResponse = null;
    // Last GeoComply transaction ID (from XML response)
    this.lastGcTransactionId = null;
    // In-memory license cache (the license is good for ~55 min; avoids refetching
    // the KV-served token on every periodic check / IP change / focus event)
    this._cachedLicense = null;
    this._licenseExpiry = null;
    // Log of recent geo events for debug panel
    this.geoEventLog = [];
    // Event listeners for debug panel (receives all events incl. failures)
    this.debugListeners = [];

    // Cross-tab storage sync
    this._storageSyncRegistered = false;
    this._storageSyncHandler = null;

    // Guards against concurrent scheduling from overlapping visibility events
    this._schedulingInProgress = false;

    // Visibility / wake-detector cleanup handles
    this._visibilityHandlersRegistered = false;
    this._visibilityHandler = null;       // shared handler ref for all window/doc events
    this._wakeDetectorId = null;          // setInterval ID for sleep-wake detection
  }

  /**
   * Subscribe to location updates.
   * @param {function} callback - Function to be called with (location, triggerType).
   * @returns {function} - Unsubscribe function.
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  subscribeDebug(callback) {
    this.debugListeners.push(callback);
    return () => {
      this.debugListeners = this.debugListeners.filter(cb => cb !== callback);
    };
  }

  _emitDebugEvent(event) {
    this._eventSeq = (this._eventSeq || 0) + 1;
    event.id = this._eventSeq; // stable key for the debug panel (expand state etc.)
    // Tester-supplied free-text reason for this check (e.g. VPN provider) — set
    // on the service before a check so the event, log and export carry it.
    if (event.reasonText === undefined) event.reasonText = this.reasonText || null;
    this.geoEventLog = [event, ...this.geoEventLog].slice(0, 50);
    this.debugListeners.forEach(cb => cb(event));
  }

  logFinancialTransaction(label) {
    const r = this.lastGeoResponse || {};
    this._emitDebugEvent({
      type: 'success',
      time: new Date().toLocaleTimeString(),
      reasonCode: 5,
      reasonName: 'FINANCIAL',
      city: r.city || r.location?.city || '--',
      state: r.state_code || r.location?.state || '--',
      ip: r.ipAddress || (typeof r.ip === 'object' ? r.ip?.ipaddress || r.ip?.address : r.ip) || '--',
      isp: r.isp || (typeof r.ip === 'object' ? r.ip?.isp : null),
      lat: r.latitude || r.location?.lat,
      lon: r.longitude || r.location?.lng,
      transaction_id: this.lastGcTransactionId || null,
      error_message: label || 'Transaction',
    });
  }

  /**
   * Initialize the SDK, fetch license, and register event handlers.
   * @param {string} userId - The current user's ID.
   */
  async initialize(userId, operator = this.operator) {
    // Already initialised for this exact user + operator — nothing to do.
    if (this.isInitialized && this.userId === userId && this.operator === operator) {
      return;
    }

    // New user or operator — clear logs and bust the cached license so the next
    // refresh fetches the license for the (possibly) new operator/cluster.
    if (this.userId !== userId || this.operator !== operator) {
      this.geoEventLog = [];
      this.lastGeoResponse = null;
      this.lastGcTransactionId = null;
      this._cachedLicense = null;
      this._licenseExpiry = null;
    }

    this.operator = operator;

    if (typeof window.GcHtml5 === 'undefined') {
      console.error('GcHtml5 SDK not loaded in window object');
      throw new Error('BrowserGuard SDK not loaded');
    }

    try {
      // 1. Create the client instance
      this.client = window.GcHtml5.createClient();
      this.userId = userId;

      // 2. Fetch license from backend
      await this.refreshLicense();
      
      // 3. Configure the client
      this.client.setUserId(userId);
      this.client.setLicense(this.license);
      // Default reason code, will be overridden by specific requests
      this.client.setReasonCode(this.client.REASON_CODE.LOGIN);

      // 4. Register event handlers (Success & Fail)
      this.registerGeolocationEventsWithPeriodicChecks();
      
      // 5. Register MyIP event handlers
      this.registerMyIpEvents();
      
      // 6. Setup visibility/focus handlers for periodic check management
      this.setupVisibilityHandlers();

      // 7. Register UX Hints (Checklist #6) - Moved back to end to ensure stability
      this.registerUxHints();

      // 8. Start Early Scan (Checklist #12) - Temporarily disabled to debug UX Hints
      // this.startEarlyScan();

      this.isInitialized = true;
      console.log('✅ BrowserGuard initialized');

    } catch (error) {
      console.error('❌ Failed to initialize BrowserGuard:', error);
      throw error;
    }
  }

  /**
   * Register UX Hints to guide users (Checklist #6)
   */
  registerUxHints() {
    if (this.client) {
      this.client.onGeoHint = (hint) => {
        const hintMsg = (typeof hint === 'string') ? hint : (hint?.message || JSON.stringify(hint));
        this.lastUxHintTime = Date.now();
        this._emitDebugEvent({
          type: 'ux_hint',
          time: new Date().toLocaleTimeString(),
          error_message: hintMsg,
        });
      };
    }
  }

  /**
   * Start early scan to reduce latency (Checklist #12)
   */
  startEarlyScan() {
    if (this.client && this.client.scan) {
      console.log('🚀 Starting early scan to warm up location engine...');
      this.client.scan((coords, errorCode) => {
        if (errorCode) {
            console.log('Early scan result:', errorCode);
        } else {
            console.log('Early scan coordinates available:', coords);
        }
      });
    }
  }

  async refreshLicense() {
    // Serve the in-memory cached license while it's still valid (~55 min TTL).
    if (this._cachedLicense && this._licenseExpiry && Date.now() < this._licenseExpiry) {
      this.license = this._cachedLicense;
      return this.license;
    }
    try {
      const response = await axios.get('/api/geocomply/license', {
        params: { userId: this.userId, operator: this.operator }
      });
      let rawLicense = response.data.license;
      
      if (rawLicense) {
        // HYPOTHESIS: The SDK expects the Base64 token inside the <license> tags,
        // NOT the full XML string, based on the integration guide example which shows a short token.
        // The backend returns XML: <license expires="...">BASE64_TOKEN</license>
        
        // 1. Try to extract content between <license ...> and </license>
        const match = rawLicense.match(/<license[^>]*>(.*?)<\/license>/s);
        if (match && match[1]) {
            console.log('Extracted token from XML license');
            this.license = match[1].trim();
        } else {
            // Fallback: Use the raw string (cleaned) if no XML tags found
            // This handles cases where backend might return just the token
            console.log('No <license> tags found, using raw string');
            this.license = rawLicense.replace(/[\r\n]+/g, '').trim();
        }
        
        console.log('License Token (first 50):', this.license.substring(0, 50));
        // Cache for 55 minutes so periodic checks reuse it.
        this._cachedLicense = this.license;
        this._licenseExpiry = Date.now() + 55 * 60 * 1000;
      } else {
        this.license = null;
      }

      return this.license;
    } catch (error) {
      console.error('Failed to refresh license:', error);
      throw error;
    }
  }

  /**
   * Trigger a geolocation request.
   * Returns a promise that resolves when the SDK 'success' or 'failed' event fires.
   */
  async requestGeolocation(reasonCode) {
    if (!this.client || !this.isInitialized) {
      return Promise.reject(new Error('BrowserGuard not initialized'));
    }

    // Clear any existing scheduled timers so a manual request resets the clock
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    // Safely refresh the license before requesting if it was wiped by a 608 error during sleep
    if (!this.license) {
        console.log('License is null (likely expired). Refreshing before geolocation request...');
        try {
            await this.refreshLicense();
        } catch (e) {
            console.error('Failed to refresh license before request:', e);
            return Promise.reject(e);
        }
    }

    return new Promise((resolve, reject) => {
      // DEV OVERRIDE FOR TESTING: Force 603 Server Communication Error
      // Uncomment this to trigger the Carbon Failover Outage!
      /*
      if (true) {
          console.warn('⚠️ FORCED OUTAGE MODE: Simulating 603 Server Communication Error.');
          setTimeout(() => {
              const code = 603; // CLNT_ERROR_SERVER_COMMUNICATION
              const message = 'Simulated connection timeout to GeoComply servers';
              
              console.error(`❌ SDK Geolocation failed: [${code}] ${message}`);
              
              // REPORT FAILURE TO BACKEND (Triggers Carbon Failover on 603)
              try {
                  axios.post('/api/geocomply/log-failure', {
                      userId: this.userId,
                      code: code,
                      message: message,
                      timestamp: new Date().toISOString()
                  }, { withCredentials: true }).catch(err => console.error('Failed to report blocking event to backend:', err));
              } catch (reportError) {
                  console.error('Failed to execute axios post:', reportError);
              }
              
              this.isStopped = true;
              if (this._pendingPromise) {
                  this._pendingPromise.resolve({ 
                      success: true, 
                      failover: true,
                      error_code: code, 
                      error_message: message 
                  });
                  this._pendingPromise = null;
              }
          }, 1500);
          return;
      }
      */

      // If there's already a high-priority (financial/manual) request in flight,
      // don't let a background interval check supersede it — skip the interval instead.
      const isIntervalCheck = reasonCode === this.client.REASON_CODE.INTERVAL || reasonCode === 3;
      if (this._pendingPromise && isIntervalCheck) {
        console.log('[BrowserGuard] Interval check skipped — higher priority request in flight.');
        resolve({ success: false, superseded: true });
        return;
      }

      // Supersede any previous pending promise
      if (this._pendingPromise) {
        this._pendingPromise.resolve({ success: false, superseded: true });
      }

      // Store the promise resolvers to be called by the event handlers
      this._pendingPromise = { resolve, reject };

      // Set parameters
      this.client.setUserId(this.userId);
      // Ensure we have a license
      if (this.license) {
        this.client.setLicense(this.license);
      }
    if (reasonCode === 'retry') {
        reasonCode = 2; // Hardcode to 2 for RETRY telemetry
    } else if (reasonCode === 'login') {
        reasonCode = this.client.REASON_CODE.LOGIN; // Restore proper native mapping so the SDK doesn't drop it!
    } else if (reasonCode === 'financial') {
        reasonCode = 5; // Hardcode to 5 for FINANCIAL / BET_PLACED telemetry
    } else if (typeof reasonCode === 'string' && /^\d+$/.test(reasonCode)) {
        reasonCode = parseInt(reasonCode, 10); // numeric reason code from the dropdown ("1" -> 1)
    }
    // (sub-codes like "9a" are passed through to the SDK as-is)
    const finalReason = reasonCode || this.client.REASON_CODE.LOGIN;
      this.client.setReasonCode(finalReason);
      this.lastReasonCode = finalReason;

      // Free-text reason via the SDK's native setReason() (recorded in the
      // geolocation transaction). Set '' to clear so it doesn't carry over.
      if (typeof this.client.setReason === 'function') {
        try { this.client.setReason(this.reasonText ? String(this.reasonText) : ''); }
        catch (e) { console.warn('Could not set reason:', e); }
      }
      
      // Flag that we started the initial check
      this.initGeolocationPerformed = true;
      this.isProcessingSuccess = false; // Reset for new request
      this.isStopped = false; // Reset the stopped flag for ALL requests (manual or wake)

      // Perform request
      console.log('📍 Requesting geolocation...');
      this.client.request({
        acceptNoCoordinates: false
      });
    });
  }

  /**
   * Register Success and Failed event handlers as per guide.
   */
  registerGeolocationEventsWithPeriodicChecks() {
    this.client.events.on('success', async (encryptedResponse_txt) => {
      console.log('✅ SDK Geolocation success. Verifying with backend...');
      this.isProcessingSuccess = true; // Mark as success processing
      
      try {
        // Send to backend for decryption
        const decryptedGeoResponse = await this.verifyWithBackend(encryptedResponse_txt);
        
        // CHECK IF BACKEND IS IN FAILOVER MODE
        // We must not treat a failover payload as a legitimate GeoComply location response
        if (decryptedGeoResponse.failover_active) {
            console.warn('⚠️ Backend returned Carbon Failover active state. Standard location checks abandoned.');
            if (this._pendingPromise) {
                // Resolve so the app can continue (login/browsing), but do not set geolocation timers
                this._pendingPromise.resolve({ success: true, failover: true });
                this._pendingPromise = null;
            }
            return; // Exit early so we don't start the standard GeoComply countdown timers
        }

        // Notify subscribers of new location
        if (decryptedGeoResponse.location && decryptedGeoResponse.error_code === 0) {
             let triggerType = 'manual';
             if (this.lastReasonCode === this.client.REASON_CODE.INTERVAL) triggerType = 'interval';
             if (this.lastReasonCode === this.client.REASON_CODE.IPCHANGE) triggerType = 'ip_change';

             this.listeners.forEach(cb => cb(decryptedGeoResponse.location, triggerType));
        }
        
        const { error_code, geolocate_in, buffer_time, troubleshooter, error_message } = decryptedGeoResponse;

        if (error_code === 0) {
          // USER PASSED
          console.log(`User passed. Next check in ${geolocate_in}s`);
          this.lastGeoResponse = decryptedGeoResponse;
          this.lastGcTransactionId = decryptedGeoResponse.gc_transaction || null;
          this._saveCheckState({
            expiresAt:    decryptedGeoResponse.expires_at,
            resumeToken:  decryptedGeoResponse.resume_token,
            location:     decryptedGeoResponse.location,
          });
          this._emitDebugEvent({
            type: 'success',
            time: new Date().toLocaleTimeString(),
            reasonCode: this.lastReasonCode,
            reasonName: reasonName(this.lastReasonCode),
            error_code,
            geolocate_in,
            buffer_time: decryptedGeoResponse.buffer_time,
            transaction_id: decryptedGeoResponse.gc_transaction || null,
            city: decryptedGeoResponse.city || decryptedGeoResponse.location?.city,
            state: decryptedGeoResponse.state_code || decryptedGeoResponse.location?.state,
            ip: decryptedGeoResponse.ipAddress || (typeof decryptedGeoResponse.ip === 'object' ? decryptedGeoResponse.ip?.ipaddress || decryptedGeoResponse.ip?.address : decryptedGeoResponse.ip) || null,
            isp: decryptedGeoResponse.isp || (typeof decryptedGeoResponse.ip === 'object' ? decryptedGeoResponse.ip?.isp : null),
            lat: decryptedGeoResponse.latitude || decryptedGeoResponse.location?.lat,
            lon: decryptedGeoResponse.longitude || decryptedGeoResponse.location?.lng,
            method: decryptedGeoResponse._source || 'sdk',
            raw: decryptedGeoResponse, // full decrypted response for the "full response" view
          });
          if(this._pendingPromise) {
            this._pendingPromise.resolve({ success: true, ...decryptedGeoResponse });
            this._pendingPromise = null;
          }

          // ... (existing MyIP and scheduling logic) ...
          if (!this.isMyIpServiceRunning) {
            this.startMyIpService();
            this.isMyIpServiceRunning = true;
          }
          if (geolocate_in > 0) {
            // Ensure buffer_time is handled, default to 0 if missing
            const buffer = buffer_time || 0;
            // Timeout = (geolocate_in * 1000) - Buffer (assuming buffer is in seconds from XML, converting to ms)
            let intervalMs = (geolocate_in * 1000) - (buffer * 1000);
            
            // Safeguard: Enforce a minimum interval (e.g. 60s) to prevent loop spamming
            const MIN_INTERVAL_MS = 10000;
            if (intervalMs < MIN_INTERVAL_MS) {
                console.warn(`[BrowserGuard] Calculated interval ${intervalMs}ms is too short. Enforcing minimum of ${MIN_INTERVAL_MS}ms.`);
                intervalMs = MIN_INTERVAL_MS;
            }

            console.log('DEBUG Timer Calc:', { geo: geolocate_in, buf: buffer, final: intervalMs });
            console.log('DEBUG: Next re-geolocation check in:', intervalMs, 'ms');
            
            if (intervalMs > 0) {
                this._schedulingInProgress = true;
                try {
                    const newLicense = await this.refreshLicense();
                    this.client.setLicense(newLicense);
                } catch (licError) {
                    console.warn('⚠️ Periodic check license refresh failed. Proceeding with existing license.', licError);
                }

                this.client.setReasonCode(this.client.REASON_CODE.INTERVAL);

                this.timerInterval = intervalMs;
                this.timerStartTime = Date.now();
                 
                this.timerId = setTimeout(async () => {
                  // FAILSAFE: Check visibility again before firing
                  if (document.hidden || (this.client && !this.client.isActive())) {
                      console.log('Timer fired but page is hidden/inactive. Pausing checks.');
                      this.timerId = null;
                      return;
                  }

                  if (!this.isStopped) {
                      await this.requestGeolocation(this.client.REASON_CODE.INTERVAL);
                  } else {
                      console.log('Skipping scheduled check because service is stopped.');
                  }
                  this.timerId = null;
                }, this.timerInterval);
                this._schedulingInProgress = false;
            }
          }
        } else {
          // USER FAILED (error_code != 0)
          // Resolve the promise but with success: false so UI can handle the "Denial" gracefully
          // instead of treating it as an "Unexpected Error" (exception).
          
          const failureData = {
              success: false,
              error_code,
              error_message,
              troubleshooter
          };

          console.warn('User failed geolocation check (Denied):', failureData);

          const troubleshooterMsg = Array.isArray(troubleshooter) && troubleshooter.length > 0
            ? troubleshooter.filter(m => m.type === 'message' || !m.type).map(m => m.content).join(' ')
            : error_message;

          this._emitDebugEvent({
            type: 'denied',
            time: new Date().toLocaleTimeString(),
            reasonCode: this.lastReasonCode,
            reasonName: reasonName(this.lastReasonCode),
            error_code,
            error_message: troubleshooterMsg || error_message,
            troubleshooter: Array.isArray(troubleshooter) ? troubleshooter : [],
            // Even on a denial the packet often carries partial signals — surface
            // whatever is available so the entry never looks "broken".
            transaction_id: decryptedGeoResponse.gc_transaction || null,
            city: decryptedGeoResponse.city || decryptedGeoResponse.location?.city || null,
            state: decryptedGeoResponse.state_code || decryptedGeoResponse.location?.state || null,
            ip: decryptedGeoResponse.ipAddress || (typeof decryptedGeoResponse.ip === 'object' ? decryptedGeoResponse.ip?.ipaddress || decryptedGeoResponse.ip?.address : decryptedGeoResponse.ip) || null,
            isp: decryptedGeoResponse.isp || (typeof decryptedGeoResponse.ip === 'object' ? decryptedGeoResponse.ip?.isp : null) || null,
            lat: decryptedGeoResponse.latitude || decryptedGeoResponse.location?.lat || null,
            lon: decryptedGeoResponse.longitude || decryptedGeoResponse.location?.lng || null,
            raw: decryptedGeoResponse, // full decrypted response for the "full response" view
          });

          this.isStopped = true; // Stop any further scheduled checks
          this.clearCheckState();
          
          if (this._pendingPromise) {
              this._pendingPromise.resolve(failureData);
              this._pendingPromise = null;
          }
          
          // Stop MyIP service
          if (this.isMyIpServiceRunning) {
            this.stopMyIpService();
          }
        }

      } catch (error) {
        console.error('Backend verification error:', error);
        
        this.isStopped = true; // Stop service on backend failure to prevent loop on visibility change
        
        if (this._pendingPromise) {
              // this._pendingPromise.resolved = true;
            this._pendingPromise.reject(error);
            this._pendingPromise = null;
        }
      }
    });

    this.client.events.on('failed', (code, message) => {
      // Checklist #8: Handle specific 6xx errors & Troubleshooting Guide
      const { ErrorCodes } = window.GcHtml5;
      
      // Map error codes to user-friendly messages or actions
      switch (code) {
        // Error 608 - LICENSE_EXPIRED
        case ErrorCodes.CLNT_ERROR_LICENSE_EXPIRED:
        case ErrorCodes.CLNT_ERROR_LICENSE_INVALID_FORMAT:
        case ErrorCodes.CLNT_ERROR_LICENSE_UNAUTHORIZED:
            console.error('License Issue (608/6xx):', message);
            console.warn('Troubleshooting 608: Token expired during sleep. Refreshing automatically...');
            this.license = null;
            this._cachedLicense = null;   // bust the cache so refreshLicense refetches
            this._licenseExpiry = null;

            if (!this.isStopped && this.initGeolocationPerformed) {
                console.log('Auto-retrying geolocation after 608 token expiration...');
                setTimeout(() => {
                    if (!this.isStopped && this.isInitialized) {
                        this.requestGeolocation(this.lastReasonCode);
                    }
                }, 500);
            }
            break;
            
        case ErrorCodes.CLNT_ERROR_CLIENT_SUSPENDED:
        case ErrorCodes.CLNT_ERROR_DISABLED_SOLUTION:
            console.error('Service Suspended:', message);
            // alert('Geolocation service is currently unavailable.'); // Removed popup
            break;
            
        case ErrorCodes.CLNT_ERROR_NETWORK_CONNECTION:
        case ErrorCodes.CLNT_ERROR_SERVER_COMMUNICATION:
            console.error('Network Error:', message);
            // Retry logic could go here
            break;
            
        // Error 609 - INVALID_CUSTOM_FIELDS
        case ErrorCodes.CLNT_ERROR_INVALID_CUSTOM_FIELDS:
            console.error('Integration Error (609): Invalid Custom Fields.', message);
            console.warn('Troubleshooting 609: Ensure custom fields object format is correct.');
            break;

        // Error 639 - REASON_CODE_MISSING
        case ErrorCodes.CLNT_ERROR_REQUIRE_REASON_CODE:
            console.error('Integration Error (639): Missing Reason Code.', message);
            console.warn('Troubleshooting 639: Always set a reason code (e.g., LOGIN, REGISTRATION) before request().');
            break;

        // Error 635 - INVALID_USER_INPUT
        // Note: SDK checks this only if solus_user_id_input_restrictions is enabled
        case 635: // Using raw code as fallback if constant missing
            console.error('Integration Error (635): Invalid User ID format.', message);
            console.warn('Troubleshooting 635: Check User ID format requirements.');
            break;
            
        // Error 611 - NO_COORDINATES
        case ErrorCodes.CLNT_ERROR_NO_COORDINATES:
            console.warn('User denied location or signal weak (No Coordinates - 611).');
            console.info('Troubleshooting 611:');
            console.info('1. Enable Wi-Fi (connect to network)');
            console.info('2. Turn on Location Services (System & Browser)');
            console.info('3. Grant Location Permissions to this site');
            break;

        // Error 612 - DOWNLOAD_REQUIRED
        case 612: 
            console.warn('Player Location Check plugin download required (612).');
            alert('Please download the GeoComply Player Location Check plugin to continue.');
            break;

        // Error 605 - USER_NOT_FOUND
        case 605:
            console.error('User Not Found (605).', message);
            console.warn('Troubleshooting 605: Ensure User ID is consistent.');
            break;
            
        default:
            // Fallback for unhandled codes
            console.warn(`Unhandled Error Code [${code}]:`, message);
            break;
      }


      // RACE CONDITION FIX:
      // The SDK sometimes emits a generic "614 Unexpected Error" simultaneously with
      // (or slightly before) the "success" event when the user is denied/restricted.
      // We want to prioritize the "success" event (which contains the granular error_code=1)
      // over this generic failure.
      
      const isGenericError = (code === 614 || code === '614');

      const handleFailure = () => {
          if (this.isProcessingSuccess) {
              console.warn(`Ignoring SDK Geolocation failure [${code}] because success was processed.`);
              return;
          }

          console.error(`❌ SDK Geolocation failed: [${code}] ${message}`);

          this._emitDebugEvent({
            type: 'sdk_error',
            time: new Date().toLocaleTimeString(),
            reasonCode: this.lastReasonCode,
            reasonName: reasonName(this.lastReasonCode),
            error_code: code,
            error_message: message,
            raw: { error_code: code, error_message: message, source: 'sdk' },
          });

          // REPORT FAILURE TO BACKEND
          try {
              axios.post('/api/geocomply/log-failure', {
                  userId: this.userId,
                  code: code,
                  message: message,
                  timestamp: new Date().toISOString()
              }, { withCredentials: true }).catch(err => console.error('Failed to report blocking event to backend:', err));
          } catch (reportError) {
              console.error('Failed to execute axios post:', reportError);
          }
          
          this.isStopped = true; // Stop any further scheduled checks
          this.clearCheckState();
          
          if (this.isMyIpServiceRunning) {
            this.stopMyIpService();
          }

          if (this._pendingPromise) {
            const isOutage = code === 603 || code === '603';
            this._pendingPromise.resolve({ 
                success: isOutage ? true : false, 
                failover: isOutage,
                error_code: code, 
                error_message: message 
            });
            this._pendingPromise = null;
          }
      };

      if (isGenericError) {
          console.log(`Received generic error [${code}]. Waiting 1000ms to see if success event fires...`);
          setTimeout(() => {
              if (this.isInitialized) handleFailure();
          }, 1000);
      } else {
          handleFailure();
      }
    });
  }

  async verifyWithBackend(encryptedData) {
    const response = await axios.post('/api/geocomply/verify', {
      encryptedData,
      userId: this.userId,
      operator: this.operator,
      reason_code: this.lastReasonCode // Force pass it to backend
    });
    return response.data;
  }

  /**
   * MyIP Service Implementation
   */
  registerMyIpEvents() {
    // Success Handler
    window.GcHtml5.onMyIpSuccess = (ip) => {
      console.log('MyIP Success: IP Changed to', ip);
      
      // Immediately acknowledge to stop further notifications
      window.GcHtml5.ackMyIpSuccess();

      if (this.client.isActive()) {
        this.handleIpChange();
      } else {
        console.log('App in background. Flagging IP change for resume.');
        this.ipChangePending = true;
      }
    };

    // Failure Handler
    window.GcHtml5.onMyIpFailure = (code, message) => {
      console.error(`MyIP Failure: [${code}] ${message}`);
      const { ErrorCodes } = window.GcHtml5;
      this.isMyIpServiceRunning = false;

      // Checklist #10: Handle specific 7xx errors
      switch (code) {
        case ErrorCodes.CLNT_ERROR_MYIP_LICENSE_NOT_SET:
            console.error('MyIP License missing. Attempting refresh...');
            this.refreshLicense().catch(e => console.error(e));
            break;
            
        case ErrorCodes.CLNT_ERROR_MYIP_STOPPED:
            console.log('MyIP service was stopped explicitly.');
            break;
            
        // Handle other specific 7xx if needed
        default:
            console.warn('Unhandled MyIP error code:', code);
            break;
      }
    };
  }

  async handleIpChange() {
      console.log('Handling IP Change Geolocation...');
      try {
          // Set custom field as per guide
          if (this.client.customFields && typeof this.client.customFields.set === 'function') {
             this.client.customFields.set("geo_ip_detect", "gc_ip_change_detect");
          } else {
             console.warn('client.customFields.set not available');
          }
          
          const license = await this.refreshLicense();
          this.client.setLicense(license);
          
          // Force Reason Code 4 (IP_CHANGE)
          await this.requestGeolocation(4);
      } catch (e) {
          console.error('Failed to handle IP change:', e);
      }
  }

  // Helper method for re-geolocation logic (Checklist #11 & #9)
  scheduleReGeolocation(delay = 0) {
     // Clear any existing timer
     if (this.timerId) {
         clearTimeout(this.timerId);
         this.timerId = null;
     }
     
      if (delay <= 0) {
          if (!this.isStopped) {
              this.client.request({ acceptNoCoordinates: false });
          }
      } else {
          this.timerInterval = delay;
           
          this.timerId = setTimeout(() => {
              // FAILSAFE: Check visibility again before firing
              if (document.hidden || (this.client && !this.client.isActive())) {
                  console.log('Timer fired but page is hidden/inactive. Pausing checks.');
                  this.timerId = null;
                  return;
              }

              if (!this.isStopped) {
                  this.requestGeolocation(this.client.REASON_CODE.INTERVAL);
              }
              this.timerId = null;
          }, this.timerInterval);
      }
  }

  async startMyIpService() {
    console.log('Starting MyIP Service...');
    try {
      const license = await this.refreshLicense();
      const myIpOptions = {
        license,
        resumable: true
      };
      window.GcHtml5.startMyIpService(myIpOptions);
    } catch (e) {
      console.error('Failed to start MyIP:', e);
    }
  }

  stopMyIpService() {
    console.log('Stopping MyIP Service...');
    if (window.GcHtml5 && window.GcHtml5.stopMyIpService) {
        window.GcHtml5.stopMyIpService();
    }
    this.isMyIpServiceRunning = false;
  }

  /**
   * Periodic Check Management (Visibility/Focus)
   */
  setupVisibilityHandlers() {
    if (this._visibilityHandlersRegistered) return;
    this._visibilityHandlersRegistered = true;
    const handleActiveChange = async () => {
        const isActive = !document.hidden && document.hasFocus();
        console.log(`Visibility Changed. Active: ${isActive}, Hidden: ${document.hidden}, Focused: ${document.hasFocus()}`);

        // App is active
        if (isActive) {
            // Handle pending IP change from background
            if (this.ipChangePending) {
                this.ipChangePending = false;
                this.handleIpChange();
                return; // Prioritize IP change over periodic check logic? Or let them race?
                // Usually IP change supersedes periodic.
            }

            console.log('handleActiveChange evaluating:', { isStopped: this.isStopped, initPerf: this.initGeolocationPerformed, timerId: this.timerId, scheduling: this._schedulingInProgress });
            if (!this.isStopped && this.initGeolocationPerformed && !this.timerId && !this._schedulingInProgress && !this._pendingPromise) {
                this._schedulingInProgress = true;
                try {
                    this.client.setUserId(this.userId);
                    const license = await this.refreshLicense();
                    this.client.setLicense(license);
                    this.client.setReasonCode(this.client.REASON_CODE.INTERVAL);

                    let remainingTime = this.getRemainingTime();
                    if (remainingTime <= 0) remainingTime = 2000; // Give Chrome 2 seconds to unfreeze WebSockets

                    this.timerStartTime = Date.now() - (this.timerInterval - remainingTime);
                    console.log(`[Timer Adjusted] StartTime shifted to maintain ${remainingTime}ms remaining`);

                    this.timerId = setTimeout(async () => {
                        // FAILSAFE: Check visibility again before firing
                        if (document.hidden || (this.client && !this.client.isActive())) {
                            console.log('Timer fired but page is hidden/inactive. Pausing checks.');
                            this.timerId = null;
                            return;
                        }

                        if (!this.isStopped) {
                            // Ensure INTERVAL reason code is passed (fallback to 3 if constant missing)
                            const reason = (this.client.REASON_CODE && this.client.REASON_CODE.INTERVAL)
                                           ? this.client.REASON_CODE.INTERVAL
                                           : 3;
                            await this.requestGeolocation(reason);
                        }
                        this.timerId = null;
                    }, remainingTime);

                } catch (e) {
                    console.error('Error rescheduling check:', e);
                } finally {
                    this._schedulingInProgress = false;
                }
            }
        } 
        // App lost focus
        else {
            if (this.timerId) {
                clearTimeout(this.timerId);
                this.timerId = null;
            }
            // Always reset scheduling flag when inactive to prevent lock-up
             
        }
    };

    const registerListeners = () => {
        this._visibilityHandler = handleActiveChange;
        ['pagehide', 'pageshow', 'focus', 'blur'].forEach(evt =>
            window.addEventListener(evt, handleActiveChange)
        );
        document.addEventListener('visibilitychange', handleActiveChange);

        // Sleep-wake detector: fires when time drifts > 10 s between 1-second ticks
        let lastTime = Date.now();
        this._wakeDetectorId = setInterval(() => {
            const currentTime = Date.now();
            if (currentTime - lastTime > 10000) {
                console.log('💻 System Wake from Sleep detected!');
                if (!this.isStopped && this.initGeolocationPerformed && !document.hidden && document.hasFocus()) {
                    if (this.timerId) { clearTimeout(this.timerId); this.timerId = null; }
                    this.requestGeolocation(this.client.REASON_CODE.INTERVAL || 3);
                } else if (!this.isStopped && this.initGeolocationPerformed && (document.hidden || !document.hasFocus())) {
                    console.log('💻 Wake detected but tab is hidden/unfocused. Resetting timers for when user returns.');
                    if (this.timerId) { clearTimeout(this.timerId); this.timerId = null; }
                    this.timerStartTime = Date.now() - this.timerInterval;
                }
            }
            lastTime = currentTime;
        }, 1000);
    };

    if (document.readyState === 'complete') {
        registerListeners();
    } else {
        window.addEventListener('load', registerListeners);
    }
  }

  // ─── Session persistence (localStorage, shared across tabs) ────────────
  //
  // Security model:
  //   • expiresAt is computed and signed server-side (HMAC-SHA256).
  //   • On every resume the signature is re-verified via /api/geocomply/validate-resume.
  //   • Tampering with expiresAt in DevTools breaks the signature → forced re-check.
  //   • Storage key is scoped per user so different accounts never share state.

  /** Storage key scoped to the current user. */
  _storageKey() {
    return this.userId ? `gc_bg_check_${this.userId}` : null;
  }

  /**
   * Persists the server-issued check state.
   * @param {number} expiresAt   - Authoritative expiry timestamp from the server.
   * @param {string} resumeToken - HMAC signature issued by the server.
   * @param {object} location    - Location data to restore on resume.
   */
  _saveCheckState({ expiresAt, resumeToken, location }) {
    const key = this._storageKey();
    if (!key || !expiresAt || !resumeToken) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        savedAt: Date.now(),
        expiresAt,
        resumeToken,
        location,
      }));
    } catch (e) {
      console.warn('[BrowserGuard] Could not persist check state to localStorage:', e);
    }
  }

  /**
   * Removes the stored check state for the current user.
   * Must be called on explicit logout — NOT on component unmount / tab close —
   * so other open tabs can still read the shared state after a page refresh.
   */
  clearCheckState() {
    const key = this._storageKey();
    if (!key) return;
    try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
  }

  /**
   * Returns milliseconds remaining until the next scheduled check.
   * Reads only the raw expiresAt — does NOT verify the signature (that happens
   * in resumeFromStoredState). Returns 0 if expired or absent.
   */
  getStoredRemainingMs() {
    const key = this._storageKey();
    if (!key) return 0;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const { expiresAt } = JSON.parse(raw);
      const remaining = expiresAt - Date.now();
      return remaining > 0 ? remaining : 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Validates the stored resume token with the server, then restores the
   * in-memory timer so the periodic check continues without a new geo request.
   *
   * If the token is missing, expired, or the signature doesn't match
   * (e.g. expiresAt was tampered with in DevTools), returns null and the
   * caller must fall through to a full requestGeolocation('login').
   *
   * Also registers a 'storage' event listener so other tabs that complete
   * a fresh check automatically reset this tab's countdown.
   *
   * @returns {Promise<object|null>} The stored location, or null on failure.
   */
  async resumeFromStoredState() {
    const key = this._storageKey();
    if (!key) return null;

    let stored;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      stored = JSON.parse(raw);
    } catch (e) {
      return null;
    }

    const { expiresAt, resumeToken, location } = stored;

    // Fast client-side expiry check before making a network call
    if (!expiresAt || !resumeToken || expiresAt <= Date.now()) {
      this.clearCheckState();
      return null;
    }

    // Server-side signature verification — cannot be bypassed client-side
    try {
      const response = await axios.post(
        '/api/geocomply/validate-resume',
        { userId: this.userId, expires_at: expiresAt, resume_token: resumeToken },
        { withCredentials: true }
      );

      if (!response.data.valid) {
        console.warn(`[BrowserGuard] Resume token rejected (${response.data.reason}). Forcing new check.`);
        this.clearCheckState();
        return null;
      }
    } catch (e) {
      // If the validation endpoint is unreachable, fail safe: force a new check
      console.warn('[BrowserGuard] Could not reach validate-resume endpoint. Forcing new check.');
      return null;
    }

    // Token is valid — restore timer state
    const remaining = expiresAt - Date.now();
    this.initGeolocationPerformed = true;
    this.timerInterval = expiresAt - stored.savedAt;
    this.timerStartTime = Date.now() - (this.timerInterval - remaining);

    if (!this.isMyIpServiceRunning) {
      this.startMyIpService();
      this.isMyIpServiceRunning = true;
    }

    this._scheduleIntervalTimer(remaining);
    this._registerStorageSync();

    console.log(`[BrowserGuard] Session resumed for "${this.userId}". Next check in ${Math.round(remaining / 1000)}s`);
    return location || null;
  }

  /** Schedules the next INTERVAL geo check after `delayMs` milliseconds. */
  _scheduleIntervalTimer(delayMs) {
    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = setTimeout(async () => {
      if (document.hidden || (this.client && !this.client.isActive())) {
        this.timerId = null;
        return;
      }
      if (!this.isStopped) {
        await this.requestGeolocation(this.client.REASON_CODE.INTERVAL);
      }
      this.timerId = null;
    }, delayMs);
  }

  /**
   * Listens for localStorage writes from other tabs.
   * When another tab saves a fresh check result our timer resets to the new interval.
   * Safe to call multiple times — only one listener is ever registered.
   */
  _registerStorageSync() {
    if (this._storageSyncRegistered) return;
    this._storageSyncRegistered = true;

    const key = this._storageKey();
    this._storageSyncHandler = (e) => {
      if (e.key !== key || !e.newValue) return;
      try {
        const updated = JSON.parse(e.newValue);
        const remaining = Math.max(updated.expiresAt - Date.now(), 0);
        if (remaining <= 0) return;

        console.log(`[BrowserGuard] Cross-tab update received. Resetting timer to ${Math.round(remaining / 1000)}s`);
        this.timerInterval = updated.expiresAt - updated.savedAt;
        this.timerStartTime = Date.now() - (this.timerInterval - remaining);
        this._scheduleIntervalTimer(remaining);
      } catch (_) { /* malformed entry — ignore */ }
    };
    window.addEventListener('storage', this._storageSyncHandler);
  }

  // ────────────────────────────────────────────────────────────────────────

  /**
   * Stop the service, clear timers, and reset state.
   */
  stop() {
    console.log('Stopping BrowserGuard Service...');
    this.isStopped = true;
    this.isInitialized = false;
    this.initGeolocationPerformed = false;
    this.isProcessingSuccess = false;

    // Clear geo-check timer and reset stale interval state
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.timerInterval = null;
    this.timerStartTime = null;
    this._schedulingInProgress = false;

    // Clear sleep-wake detector interval
    if (this._wakeDetectorId) {
      clearInterval(this._wakeDetectorId);
      this._wakeDetectorId = null;
    }

    // Remove window/document visibility listeners
    if (this._visibilityHandler) {
      ['pagehide', 'pageshow', 'focus', 'blur'].forEach(evt =>
        window.removeEventListener(evt, this._visibilityHandler)
      );
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._visibilityHandlersRegistered = false;

    // Remove cross-tab storage listener
    if (this._storageSyncHandler) {
      window.removeEventListener('storage', this._storageSyncHandler);
      this._storageSyncHandler = null;
    }
    this._storageSyncRegistered = false;

    this.stopMyIpService();

    // Clear event log and last response so the next session always starts clean
    this.geoEventLog = [];
    this.lastGeoResponse = null;
    this.lastGcTransactionId = null;
    this.userId = null;
    this.operator = null;
  }

  /**
   * Full re-initialization for retry after a failure.
   * Destroys the old SDK client and builds a completely fresh one.
   */
  async resetForRetry() {
    console.log('[BrowserGuard] Full reset for retry...');

    // Clear timers
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.stopMyIpService();

    // Reset all state
    this.isStopped = false;
    this.isProcessingSuccess = false;
    this.initGeolocationPerformed = false;
    this._pendingPromise = null;
    this.timerStartTime = null;
    this.timerInterval = null;
    this.isInitialized = false; // Force full re-init
    this._storageSyncRegistered = false;

    // Re-initialize completely (fetches fresh license, new client instance)
    await this.initialize(this.userId);

    console.log('[BrowserGuard] Reset complete.');
  }

  getRemainingTime() {
    if (this.timerStartTime && this.timerInterval) {
        const elapsed = Date.now() - this.timerStartTime;
        console.log(`[Timer] Resuming: Interval=${this.timerInterval}ms, Elapsed=${elapsed}ms, Remaining=${this.timerInterval - elapsed}ms`);
        const remaining = this.timerInterval - elapsed;
        return remaining > 0 ? remaining : 0;
    }
    return 0;
  }
}

// Export singleton
const browserGuardService = new BrowserGuardService();
export default browserGuardService;
